import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
const STORAGE_KEY = 'possignin-theme';

function resolve(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return mode;
}

function apply(mode: ThemeMode) {
  const resolved = resolve(mode);
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(resolved);
  document.documentElement.dataset['themeMode'] = mode;
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'system';
    return (localStorage.getItem(STORAGE_KEY) as ThemeMode) ?? 'system';
  });

  // Apply on mount and when system preference changes (only when mode === 'system')
  useEffect(() => {
    apply(mode);
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  function setMode(m: ThemeMode) {
    localStorage.setItem(STORAGE_KEY, m);
    setModeState(m);
  }

  function toggle() {
    const resolved = resolve(mode);
    setMode(resolved === 'dark' ? 'light' : 'dark');
  }

  return { mode, resolved: resolve(mode), setMode, toggle };
}
